import type {
  Catchup,
  EpgInfoDto,
  TvChannel,
  TvShow,
  TvShowEpisode,
  TvShowSeason,
  Vod,
} from '@setplex/tria-api'
import type { TFunction } from 'i18next'
import type { CatchupProgramFormatted, PlayingEvent } from '~/shared/interfaces'
import type { PlayerAllSearchParams } from '~/shared/interfaces/player/generic'
import type { PlayerTitle } from './index.h'

import { ScheduleType } from '@setplex/tria-api'
import { model as router } from '~/core/router'
import { getStartEndWithRewindLength } from '~/entities/player/tv/lib'
import { getProgramTitle } from '~/shared/formatters/tv'
import {
  formatEpgSubtitle,
  formatTimeRangeUnified,
} from '~/shared/helpers/date'
import { addSearchParamsForPath, getSearchParams } from '~/shared/tools/player'

export const getTitleVod = ({
  searchParam,
  currentVod,
}: {
  searchParam: string
  currentVod: Vod | null
}): PlayerTitle => {
  const { vodId } = getSearchParams(searchParam)

  let title = ''
  if (vodId) title = vodId === currentVod?.id ? `${currentVod?.name}` : ''

  return { title, subtitle: '' }
}

export const getTitleCatchup = ({
  searchParam,
  currentCatchup,
  currentProgram,
}: {
  searchParam: string | null
  currentCatchup: Catchup | null
  currentProgram: CatchupProgramFormatted | null
}): PlayerTitle => {
  const { catchupId } = getSearchParams(searchParam ?? '')
  const { startMs, endMs } = currentProgram || {}

  let subtitle = ''
  let title

  if (catchupId && catchupId === currentCatchup?.id) {
    title =
      currentCatchup.scheduleType === ScheduleType.HOURLY
        ? currentCatchup?.channel?.name
        : currentProgram?.name
  }

  if (startMs && endMs) {
    subtitle = formatTimeRangeUnified(startMs, endMs, {
      alwaysIncludeDate: true,
    })
  }

  return { title: title ?? '', subtitle }
}

const getShowSubtitle = ({
  seasonNumber,
  episodeNumber,
  episodeName = '',
  t,
}: {
  t: TFunction
  seasonNumber?: string
  episodeNumber?: string
  episodeName?: string
}): string => {
  if (seasonNumber && episodeNumber) {
    const subtitleTranslation = t('season_episode_shortened', {
      episode_number: episodeNumber,
      season_number: seasonNumber,
    })
    return `${subtitleTranslation} \xa0\xa0 ${episodeName}`
  }

  if (episodeNumber) {
    const subtitleTranslation = t('episode_shortened', {
      number: episodeNumber,
    })
    return `${subtitleTranslation} \xa0\xa0 ${episodeName}`
  }

  return ''
}

export const getTitleShow = ({
  searchParam,
  currentTvShow,
  currentSeason,
  currentEpisode,
  isTrailer,
  t,
}: {
  searchParam: string
  currentTvShow: TvShow | null
  currentSeason: TvShowSeason | null
  currentEpisode: TvShowEpisode | null
  isTrailer: boolean
  t: TFunction
}): PlayerTitle => {
  const { tvShowId } = getSearchParams(searchParam)

  let title: string | undefined = undefined
  let subtitle: string | undefined = undefined

  if (isTrailer) {
    title = currentTvShow?.name
    return { title, subtitle }
  }

  if (!tvShowId || tvShowId !== currentTvShow?.id) return { title, subtitle }

  const { displayNumber: episodeNumber, name: episodeName } =
    currentEpisode || {}
  const { displayNumber: seasonNumber } = currentSeason || {}

  title = currentTvShow?.name
  subtitle = getShowSubtitle({ seasonNumber, episodeNumber, episodeName, t })

  return { title, subtitle }
}

export const getTitleTv = ({
  searchParam,
  currentTvChannel,
  program,
  rewindLengthSec,
  isHiddenSubtitle,
}: {
  searchParam: string
  currentTvChannel: TvChannel | null
  program: EpgInfoDto | null
  rewindLengthSec?: number | null
  isHiddenSubtitle?: boolean
}): PlayerTitle => {
  const { channelId } = getSearchParams(searchParam)
  const { id, name, number, channelNumber } = currentTvChannel || {}

  const numberToDisplay = channelNumber || number

  const title =
    channelId != null && Number(channelId) === id && name
      ? `${numberToDisplay}. ${name}`
      : ''

  if (!program || isHiddenSubtitle) return { title, subtitle: '' }

  const { title: programTitle } = program

  const { startTime, endTime } = getStartEndWithRewindLength({
    rewindLengthSec,
    program,
  })

  const subtitle = getProgramTitle({
    programTitle,
    startTimeMs: startTime * 1000,
    endTimeMs: endTime * 1000,
  })

  return { title, subtitle }
}

export const getTitleEvent = ({
  currentLiveEvent,
}: {
  currentLiveEvent: PlayingEvent | null
}): PlayerTitle => {
  const { liveEventStart, description, liveEventEnd, status } =
    currentLiveEvent || {}

  let title = ''
  let subtitle = ''

  if (description) {
    title = `${description || ''}`
  }

  if (!liveEventStart || !liveEventEnd) return { title, subtitle }

  subtitle = formatEpgSubtitle(status, liveEventStart, liveEventEnd)

  return { title, subtitle }
}

export const navigateTo = (
  path: string,
  searchParams: PlayerAllSearchParams,
  state?: unknown
) => {
  const pathObj = addSearchParamsForPath({
    params: searchParams,
    path,
  })
  return router.navigateReplace({
    pathname: pathObj,
    state,
  })
}
